import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { Button, Flex, Form, Input, Spin, Typography } from 'antd';
import AuthPageShell from '@/components/auth/AuthPageShell';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { verifyLeadIdentity } from '@/api/mcp-crm/mutations';
import { getApiErrorMessage } from '@/utils/apiError';

const { Text } = Typography;

export const Route = createFileRoute('/v/$publicToken')({
  component: RouteComponent
});

function RouteComponent() {
  const { publicToken } = useParams({ from: '/v/$publicToken' });
  const { t } = useTranslation(['auth', 'common']);
  const [status, setStatus] = useState<'idle' | 'verifying' | 'code' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [submittingCode, setSubmittingCode] = useState(false);

  useEffect(() => {
    if (!publicToken) {
      setStatus('error');
      setErrorMessage(t('auth:verifyLead.invalidToken'));
      return;
    }

    let cancelled = false;
    const run = async () => {
      setStatus('verifying');
      try {
        const result = await verifyLeadIdentity({ publicToken });
        if (cancelled) return;
        if (result.codeRequired) {
          setStatus('code');
          return;
        }
        setStatus('success');
      } catch (error: unknown) {
        if (cancelled) return;
        setStatus('error');
        setErrorMessage(getApiErrorMessage(error, t('auth:verifyLead.failed')));
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [publicToken, t]);

  const onCodeFinish = async (values: { code: string }) => {
    try {
      setSubmittingCode(true);
      await verifyLeadIdentity({ publicToken, code: values.code });
      setStatus('success');
    } catch (error: unknown) {
      setErrorMessage(getApiErrorMessage(error, t('auth:verifyLead.invalidCode')));
    } finally {
      setSubmittingCode(false);
    }
  };

  const renderContent = () => {
    if (status === 'verifying' || status === 'idle') {
      return (
        <Flex vertical align="center" gap={16}>
          <Spin size="large" />
          <Text>{t('auth:verifyLead.verifying')}</Text>
        </Flex>
      );
    }

    if (status === 'success') {
      return (
        <Flex vertical align="center" gap={16}>
          <Text>{t('auth:verifyLead.success')}</Text>
          <Link to="/login">
            <Button type="primary">{t('auth:verifyLead.backToLogin')}</Button>
          </Link>
        </Flex>
      );
    }

    if (status === 'code') {
      return (
        <Flex vertical gap={16}>
          <Text>{t('auth:verifyLead.enterCode')}</Text>
          <Form name="lead-identity-code" onFinish={onCodeFinish} layout="vertical">
            <Form.Item
              name="code"
              rules={[
                { required: true, message: t('auth:verifyLead.codeRequired') },
                { len: 6, message: t('auth:verifyLead.codeRequired') }
              ]}
            >
              <Input.OTP length={6} style={{ width: '100%', justifyContent: 'center' }} />
            </Form.Item>
            {errorMessage ? <Text type="danger">{errorMessage}</Text> : null}
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submittingCode} block>
                {t('auth:verifyLead.submitCode')}
              </Button>
            </Form.Item>
          </Form>
        </Flex>
      );
    }

    return (
      <Flex vertical align="center" gap={16}>
        <Text type="danger">{errorMessage}</Text>
        <Link to="/login">{t('auth:verifyLead.backToLogin')}</Link>
      </Flex>
    );
  };

  return (
    <AuthPageShell title={t('auth:verifyLead.title')}>
      {renderContent()}
    </AuthPageShell>
  );
}
